import React, { useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../../contexts/LanguageContext';

interface WebcamCaptureProps {
  onCapture: (file: File) => void;
  onClose: () => void;
  showGuideFrame?: boolean; // Optional prop to show document guide frame
  guideAspectRatio?: number; // width / height for the guide frame (e.g. 1.586 for ID card, 0.707 for A4 portrait)
  allowFlip?: boolean; // show a front/back camera switch (e.g. for the doctor's photo)
}

const WebcamCapture: React.FC<WebcamCaptureProps> = ({
  onCapture,
  onClose,
  showGuideFrame = false,
  guideAspectRatio,
  allowFlip = false,
}) => {
  const { t } = useLanguage();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');

  const startCamera = useCallback(async (mode: 'environment' | 'user') => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode },
        audio: false,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setIsStreaming(true);
        setError(null);
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError(t('camera_access_denied') || 'Camera access denied. Please allow camera permissions.');
    }
  }, [t]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsStreaming(false);
  }, []);

  const flipCamera = useCallback(() => {
    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
  }, []);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d');
    if (!context) return;

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
        stopCamera();
        onCapture(file);
        onClose();
      }
    }, 'image/jpeg', 0.95);
  }, [stopCamera, onCapture, onClose]);

  const handleClose = useCallback(() => {
    stopCamera();
    onClose();
  }, [stopCamera, onClose]);

  React.useEffect(() => {
    startCamera(facingMode);
    return () => {
      stopCamera();
    };
  }, [facingMode, startCamera, stopCamera]);

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black flex flex-col">
      {/* Close Button */}
      <button
        onClick={handleClose}
        className="absolute top-4 right-4 z-10 w-12 h-12 flex items-center justify-center bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full transition-colors"
      >
        <span className="text-2xl">✕</span>
      </button>

      {/* Video Preview */}
      <div className="flex-1 relative overflow-hidden">
        {error ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center p-6 bg-gray-900 bg-opacity-80 rounded-xl">
              <p className="text-red-400 mb-4 text-lg">{error}</p>
              <button
                onClick={() => startCamera(facingMode)}
                className="px-8 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 font-semibold transition-colors"
              >
                {t('try_again') || 'Try Again'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            />
            <canvas ref={canvasRef} className="hidden" />

            {/* Document Guide Frame - Only show if enabled */}
            {isStreaming && showGuideFrame && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none p-6">
                <div
                  className="border-4 border-white border-dashed rounded-xl opacity-60 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]"
                  style={
                    guideAspectRatio && guideAspectRatio >= 1
                      ? { width: 'min(90%, 32rem)', aspectRatio: String(guideAspectRatio) }
                      : { height: 'min(70%, 32rem)', aspectRatio: String(guideAspectRatio ?? 2 / 3) }
                  }
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Controls */}
      {isStreaming && !error && (
        <div className="bg-black bg-opacity-50 p-6">
          <div className="flex items-center justify-center gap-8">
            {/* Cancel Button */}
            <button
              onClick={handleClose}
              className="w-14 h-14 flex items-center justify-center bg-gray-700 hover:bg-gray-600 rounded-full transition-colors"
              title={t('cancel') || 'Cancel'}
            >
              <span className="text-white text-xl">✕</span>
            </button>

            {/* Capture Button */}
            <button
              onClick={capturePhoto}
              className="w-20 h-20 flex items-center justify-center bg-white hover:bg-gray-200 rounded-full shadow-lg transition-transform hover:scale-105 relative"
            >
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-sm">{t('capture') || 'Capture'}</span>
              </div>
            </button>

            {/* Flip camera (front/back) — e.g. for the doctor's photo */}
            {allowFlip ? (
              <button
                onClick={flipCamera}
                className="w-14 h-14 flex items-center justify-center bg-gray-700 hover:bg-gray-600 rounded-full transition-colors"
                title={t('flip_camera') || 'Flip camera'}
              >
                <span className="text-white text-2xl">🔄</span>
              </button>
            ) : (
              <div className="w-14 h-14"></div>
            )}
          </div>

          {showGuideFrame && (
            <p className="text-center text-white text-sm mt-4 opacity-75">
              {t('align_document_in_frame') || 'Align the document within the frame'}
            </p>
          )}
        </div>
      )}
    </div>,
    document.body
  );
};

export default WebcamCapture;
